import { useIntl } from 'react-intl'

import { Button } from '@/components/atoms/Button/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  type DialogProps,
  DialogTitle,
} from '@/components/atoms/Dialog/Dialog'
import { InlineError } from '@/components/atoms/InlineError'
import { type PromotionOption } from '@/components/model/PromotionOption'
import { PromotionOptionSummary } from '@/components/molecules/PromotionOptionSummary'

export interface RemoveFromPlanDialogProps extends Pick<DialogProps, 'open' | 'onOpenChange'> {
  readonly option?: PromotionOption
  readonly promotions: ReadonlyArray<{ readonly id: string; readonly partnerName: string }>
  readonly locked?: boolean
  readonly pending?: boolean
  readonly error?: string
  readonly onConfirm: () => void
}

export const RemoveFromPlanDialog = ({
  open,
  onOpenChange,
  option,
  promotions,
  locked,
  pending,
  error,
  onConfirm,
}: RemoveFromPlanDialogProps) => {
  const intl = useIntl()
  const count = promotions.length
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[512px]">
        <DialogTitle>{intl.formatMessage({ id: 'promotions.shop.removeDialog.title' })}</DialogTitle>
        {option && <PromotionOptionSummary placement={option.placement} fee={option.fee} />}
        {!locked && (
          <DialogDescription className="pb-0 text-sm leading-[1.4] text-gray-800 select-text">
            {intl.formatMessage({ id: 'promotions.shop.removeDialog.free' })}
            {count === 0 && <> {intl.formatMessage({ id: 'promotions.shop.removeDialog.readd' })}</>}
          </DialogDescription>
        )}
        {!locked && count > 0 && (
          <div className="flex flex-col gap-2 rounded-[4px] border border-orange-200 bg-orange-200/20 px-4 py-3 text-sm">
            <p className="font-bold text-orange-800">
              {intl.formatMessage({ id: 'promotions.shop.removeDialog.promotions' }, { count })}
            </p>
            <ul className="flex list-disc flex-col gap-1 pl-[18px] text-gray-800">
              {promotions.map(({ id, partnerName }) => (
                <li key={id}>{partnerName}</li>
              ))}
            </ul>
            <p className="text-gray-500">
              {intl.formatMessage({ id: 'promotions.shop.removeDialog.otherOptions' }, { count })}
            </p>
          </div>
        )}
        {error && <InlineError message={error} />}
        <DialogFooter className="pt-2">
          <Button size="sm" className="px-6" disabled={pending} onClick={() => onOpenChange?.(false)}>
            {intl.formatMessage({ id: 'promotions.shop.dialog.cancel' })}
          </Button>
          <Button size="sm" className="px-6" variant="critical" loading={pending} disabled={locked} onClick={onConfirm}>
            {intl.formatMessage({ id: 'promotions.shop.removeDialog.confirm' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
